<template>
  <div class="product">
    <div class="product__image-block image-block">
      <div class="image-block__product-labels product-labels">
        <div class="product-labels__product-info-labels product-info-labels">
          <product-tag
            v-if="isRecommended"
            :is-product-recommended="isRecommended"
            :message="$t('product.drSelectionMessage')"
            class="product-info-labels__recommended-label product-label"
          ></product-tag>
          <span
            v-for="(productCategory, index) of productCategories"
            :key="productCategory + index"
            class="product-info-labels__category-label product-label"
            >{{ $t(productCategory) }}</span
          >
        </div>

        <span
          v-if="shouldShowProductSizesLabel"
          class="product-labels__product-sizes-info"
          @mouseover="onProductHover"
          >{{ $t('product.2sizesMessage') }}</span
        >

        <product-tag
          v-if="isAvailabilityTagVisible"
          :is-product-available="isAnySizeOfProductAvailable"
          :is-product-in-stock="isAnySizeOfProductInStock"
          :message="statusTagMessage"
          class="product-labels__status-label product-label"
        />
      </div>

      <responsive-image
        :name="productImage"
        fit-object
        :image-lazy="imageLazy"
        :image-height-to-width-ratio="$options.imageHeightToWidthRatio"
        class="image-block__product-image"
        @mouseover.native="onProductHover"
        @click.native="goToProductPage"
      />

      <transition v-if="!isMobileDevice" name="fade">
        <div
          v-show="isProductHovered"
          class="image-block__product-image-overlay"
          @mouseleave="onProductLeave"
          @click="goToProductPage"
        >
          <responsive-image
            :name="overlayProductImage"
            fit-object
            :image-lazy="imageLazy"
            :image-height-to-width-ratio="$options.imageHeightToWidthRatio"
          />

          <button
            v-if="isAddToCartButtonVisible"
            class="button-add-to-cart flex--center--center"
            :class="addToCartButtonClasses"
            @click.stop="onAddProductToCart"
          >
            <span v-if="isInCart" class="custom-icon icon-check-circle"></span>
            {{ $t(addToCartButtonLabel) }}
          </button>
        </div>
      </transition>
    </div>

    <div class="product__description product-description">
      <div class="product-description__header product-description-header">
        <h4 class="product-description-header__title u-typography-helvetica u-text u-text--m">
          {{ productTitle }}
        </h4>
        <div
          v-if="isSerumProduct"
          class="product-description-header__ingredients-list ingredients-list"
        >
          <span
            v-for="(colorBoxStyle, index) of ingredientsBoxStyles"
            :key="`${colorBoxStyle}-${index}`"
            class="ingredients-list__item"
            :style="colorBoxStyle"
          ></span>
        </div>
      </div>

      <p class="product-description__text u-typography-helvetica u-text u-text--xs">
        {{ $t(productSubtitle) }}
      </p>
      <p
        v-if="productPriceValue"
        class="product-description__price u-typography-helvetica u-text u-text--s"
      >
        {{ productPriceFormatted }}
      </p>
    </div>
    <sidebar-popup v-if="!isMobileDevice" ref="productSizeInfoPopup">
      <product-size-options
        :selected-product-option="selectedProductOption"
        :product-options="productOptions"
        :product-title="productTitle"
        @product-option-select="onProductOptionSelect"
        @add-to-cart="addProductToCart"
      />
    </sidebar-popup>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';

import { productConfigMixin } from '@/mixins/productConfigMixin';
import { priceFormatterMixin } from '@/mixins/priceFormatterMixin';

import ResponsiveImage from '@/components/common/ResponsiveImage.vue';
import ProductTag from '@/components/common/ProductTag.vue';

import {
  getProductPackSize,
  getProductPackCategories,
  getProductPackPrice,
  isProductPackSerum,
  isProductPackGenericEyeSerum,
  getProductPackItems,
  isProductPackAvailable,
  isProductPackInStock,
  getProductPackReference
} from '@/models/core/ProductPack';
import { getPriceValue, getSizeUnits, getSizeValue } from '@/models/core/values';
import { getPackItemIngredients } from '@/models/core/PackItem';
import { getIngredientName } from '@/models/core/Ingredient';
import {
  getProductOptionsDefaultOption,
  getProductOptionsProductPacksList,
  getProductOptionsOptionByFormulaIdOrDefault
} from '@/models/core/ProductOptions';

import { PRODUCT_CATEGORY_LABEL } from '@/constants/treatment';

import { COUNTRY_ISO } from '@/constants/countries';

import { types } from '@/store/types';

const IMAGE_HEIGHT_TO_WIDTH_RATIO = 1.46;

const COUNTRIES_WITH_DMAE_RESTRICTIONS = [
  COUNTRY_ISO.AUSTRALIA.toLowerCase(),
];

export default {
  name: 'AppProduct',
  components: {
    ResponsiveImage,
    ProductTag,
    SidebarPopup: () => import('@/components/common/SidebarPopup.vue'),
    ProductSizeOptions: () => import('@/components/common/ProductSizeOptions.vue')
  },
  mixins: [productConfigMixin, priceFormatterMixin],
  imageHeightToWidthRatio: IMAGE_HEIGHT_TO_WIDTH_RATIO,
  props: {
    productName: {
      type: String,
      required: true
    },
    imageLazy: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isProductHovered: false,
      selectedProductReference: null
    };
  },
  computed: {
    ...mapState({
      hashedDataId: types.state.HASHED_DATA_ID,
      doctorSlug: types.state.DOCTOR_SLUG,
      selectedEyeSerumGenericFormulaId: types.state.SELECTED_EYE_SERUM_GENERIC_FORMULA_ID
    }),
    ...mapGetters({
      allProductOptions: types.getters.ALL_PRODUCT_OPTIONS_SETS,
      getIsProductRecommended: types.getters.GET_IS_PRODUCT_RECOMMENDED,
      isProductInCart: types.getters.GET_IS_PRODUCT_PACK_ADDED_TO_CART,
      cartItems: types.getters.CART_ITEMS,
      getProductFromCart: types.getters.GET_PRODUCT_FROM_CART,
      deliveryCountry: types.getters.DELIVERY_COUNTRY,
      getProductPackByReference: types.getters.GET_PRODUCT_PACK_BY_REFERENCE
    }),
    selectedProductOption: {
      get() {
        if (!this.selectedProductReference) {
          const productOptions = this.allProductOptions[this.productName];
          const defaultOption = getProductOptionsDefaultOption(productOptions);

          if (isProductPackGenericEyeSerum(defaultOption)) {
            return this.setEyeContourGenericOption(productOptions);
          }

          return defaultOption;
        }

        return this.getProductPackByReference(this.selectedProductReference);
      },
      set(productPack) {
        this.selectedProductReference = getProductPackReference(productPack);
      }
    },
    productOptions() {
      const options = this.allProductOptions[this.productName];

      return getProductOptionsProductPacksList(options);
    },
    productImage() {
      return this.getProductImage({
        product: this.selectedProductOption,
        hasProductSizeOptions: this.hasProductSizeOptions
      });
    },
    overlayProductImage() {
      return `${this.productName}Hover`;
    },
    productConfiguration() {
      return this.getProductConfiguration(this.selectedProductOption);
    },
    productTitle() {
      const { title, titleRecommended } = this.productConfiguration;

      return this.isRecommended && titleRecommended ? this.$t(titleRecommended) : this.$t(title);
    },
    productDescription() {
      return this.getProductDescription({
        product: this.selectedProductOption
      });
    },
    productSubtitle() {
      const value = getSizeValue(this.productSize);
      const units = getSizeUnits(this.productSize);

      return `${this.$t(this.productDescription)}, ${value}${units}`;
    },
    productSize() {
      return getProductPackSize(this.selectedProductOption);
    },
    productSizeOptions() {
      return this.getProductSizeOptions(this.productOptions);
    },
    productCategories() {
      const productCategories = getProductPackCategories(this.selectedProductOption);
      const productCategoryLabels = productCategories.map(
        category => PRODUCT_CATEGORY_LABEL[category]
      );

      return productCategoryLabels;
    },
    hasProductSizeOptions() {
      if (this.selectedProductOption && isProductPackGenericEyeSerum(this.selectedProductOption)) {
        // generic eye serum has more than one option, but they are not sizes
        return false;
      }

      return this.productOptions.length > 1;
    },
    productPrice() {
      return getProductPackPrice(this.selectedProductOption);
    },
    productPriceValue() {
      return getPriceValue(this.productPrice);
    },
    productPriceFormatted() {
      if (this.productPrice) {
        return this.formatPrice(this.productPrice);
      }

      return '';
    },
    isRecommended() {
      return this.getIsProductRecommended(this.productName);
    },
    isSerumProduct() {
      return isProductPackSerum(this.selectedProductOption);
    },
    isEyeContourGeneric() {
      return isProductPackGenericEyeSerum(this.selectedProductOption);
    },
    shouldShowRecommendedTag() {
      return !this.isSerumProduct && this.isRecommended;
    },
    firstBottle() {
      const [firstBottle] = getProductPackItems(this.selectedProductOption);

      return getPackItemIngredients(firstBottle);
    },
    secondBottle() {
      const [, secondBottle] = getProductPackItems(this.selectedProductOption);

      if (secondBottle) {
        return getPackItemIngredients(secondBottle);
      }

      return [];
    },
    ingredientsBoxStyles() {
      return [...this.firstBottle, ...this.secondBottle].map(ingredient => {
        const name = getIngredientName(ingredient);

        return {
          backgroundColor: `var(--u-color-${name})`
        };
      });
    },
    isInCart() {
      return this.productOptions.some(productOption => this.isProductInCart(productOption));
    },
    addToCartButtonLabel() {
      const notInCartLabel = this.isEyeContourGeneric ? 'action.selectFormula' : 'action.addToCart';

      return this.isInCart ? 'action.inYourCart' : notInCartLabel;
    },
    addToCartButtonClasses() {
      return { 'button-add-to-cart--white': this.isInCart };
    },
    isAddToCartButtonVisible() {
      let disabledByDMAE = false;
      // Business Rule : check if DMAE is an ingredient
      if(COUNTRIES_WITH_DMAE_RESTRICTIONS.includes(this.deliveryCountry) && (isProductPackSerum(this.selectedProductOption) || isProductPackGenericEyeSerum(this.selectedProductOption))) {
            [...this.firstBottle, ...this.secondBottle].forEach(ingredient => {
           const name = getIngredientName(ingredient);
           if( name==='dmae')  disabledByDMAE = true;
          });
      }
       return this.isAnySizeOfProductInStock && this.isAnySizeOfProductAvailable && !disabledByDMAE; 
    },
    isAvailabilityTagVisible() {
      return !this.isAnySizeOfProductInStock || !this.isAnySizeOfProductAvailable;
    },
    isAnySizeOfProductAvailable() {
      return this.productOptions.some(product => isProductPackAvailable(product));
    },
    isAnySizeOfProductInStock() {
      return this.productOptions.some(product => isProductPackInStock(product));
    },
    shouldShowProductSizesLabel() {
      return !this.isAvailabilityTagVisible && this.hasProductSizeOptions && !this.isProductHovered;
    },
    statusTagMessage() {
      return !this.isAnySizeOfProductAvailable
        ? this.$t('product.comingSoonMessage')
        : this.$t('product.outOfStockMessage');
    },
    isMobileDevice() {
      return this.$windowWidth.value < 960;
    }
  },
  watch: {
    cartItems() {
      if (this.isInCart && this.hasProductSizeOptions) {
        this.setProductOptionFromCart();
      }
    }
  },
  created() {
    if (this.isInCart && this.hasProductSizeOptions) {
      this.setProductOptionFromCart();
    }
  },
  methods: {
    ...mapActions({
      addProductPackInCartOrOpenCart: types.actions.PUT_PRODUCT_PACK_IN_CART_OR_OPEN_CART
    }),
    onProductHover() {
      this.isProductHovered = true;
    },
    onProductLeave() {
      this.isProductHovered = false;
    },
    onAddProductToCart() {
      if (this.isEyeContourGeneric && !this.isInCart) {
        this.goToProductPage();

        return;
      }

      if (this.hasProductSizeOptions && !this.isInCart) {
        this.openProductSizeInfoPopup();

        return;
      }

      this.addProductPackInCartOrOpenCart(this.selectedProductOption);
    },
    addProductToCart() {
      this.addProductPackInCartOrOpenCart(this.selectedProductOption);
    },
    onProductOptionSelect(product) {
      this.selectedProductOption = product;
    },
    setProductOptionFromCart() {
      const productPack = this.getProductFromCart(this.productName);

      this.selectedProductOption = productPack;
    },
    setEyeContourGenericOption(productOptions) {
      return this.isInCart
        ? this.getProductFromCart(this.productName)
        : getProductOptionsOptionByFormulaIdOrDefault(
            this.selectedEyeSerumGenericFormulaId,
            productOptions
          );
    },
    async goToProductPage() {
      await this.$router.push({
        name: 'ProductDetailsPage',
        params: {
          productName: this.productName,
          hashedDataId: this.hashedDataId,
          doctorSlug: this.doctorSlug
        }
      });
    },
    openProductSizeInfoPopup() {
      this.$refs.productSizeInfoPopup.open();
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/icon.scss';

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.product {
  display: flex;
  flex-direction: column;

  &__image-block {
    width: 100%;
    margin-bottom: 16px;
    position: relative;
  }
}

.product-description-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;

  &__title {
    margin-inline-end: 10px;
  }

  &__ingredients-list {
    margin: 4px 0;
  }
}

.ingredients-list {
  display: flex;

  &__item {
    display: block;
    width: 13px;
    height: 13px;
    border-radius: 2px;

    &:not(:last-child) {
      margin-inline-end: 4px;
    }
  }
}

.button-add-to-cart {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60px;
  padding: 0 10px;
  background-color: var(--u-color-grey-900);
  color: var(--u-color-grey-50);
  border: none;
  font-family: var(--u-font-family-Gilroy);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.75px;
  cursor: pointer;
  z-index: 4;

  &:hover {
    color: var(--u-color-grey-100);
  }

  &--white {
    background-color: var(--u-color-white);
    color: var(--u-color-grey-900);
    border: 1px solid var(--u-color-grey-900);
    &:hover {
      color: var(--u-color-grey-900);
    }
  }
}

.icon-check-circle {
  width: 20px;
  height: 20px;
  margin-inline-end: 10px;
}

.product-description {
  &__title {
    margin-bottom: 4px;
    color: var(--u-color-graphite);
  }

  &__text {
    margin-bottom: 4px;
    color: var(--u-color-grey-600);
  }

  &__price {
    color: var(--u-color-grey-900);
  }
}

.image-block {
  &__product-image,
  &__product-image-overlay {
    height: 100%;
    width: 100%;
    cursor: pointer;
  }

  &__product-image-overlay {
    position: absolute;
    top: 0px;
    left: 0px;
    right: 0px;
    z-index: 2;
  }
}

.product-label {
  --product-label-font-size: 8px;
  --product-label-line-height: 10px;
  margin-bottom: 8px;
  padding: 4px 6px;
  font-family: var(--u-font-family-Helvetica);
  font-size: var(--unsafe-label-font-size, var(--product-label-font-size));
  line-height: var(--unsafe-label-line-height, var(--product-label-line-height));
  letter-spacing: 0.4px;
  text-transform: uppercase;
  text-align: center;
  border-radius: 3px;
}

.product-labels {
  display: flex;
  align-items: flex-end;

  &__product-info-labels,
  &__status-label,
  &__product-sizes-info {
    position: absolute;
    z-index: 3;
  }

  &__product-info-labels {
    top: 8px;
    right: 8px;
    left: 8px;
  }

  &__product-sizes-info {
    width: 100%;
    bottom: 16px;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0.4px;
    color: var(--u-color-grey-600);
    text-align: center;
  }

  &__status-label {
    left: 8px;
    bottom: 8px;
    margin: 0;
  }
}

.product-info-labels {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;

  &__category-label {
    margin-inline-start: 8px;
    color: var(--u-color-grey-900);
    background-color: var(--u-color-white);
  }
}

@media (min-width: 760px) {
  .product-label {
    --product-label-font-size: 12px;
    --product-label-line-height: 15px;
  }
}

@media (min-width: 960px) {
  .ingredients-list {
    &__item {
      width: 20px;
      height: 20px;
      border-radius: 4px;
    }
  }

  .product-labels {
    &__product-sizes-info {
      font-size: 16px;
      line-height: 28px;
    }
  }

  .product-label {
    --product-label-font-size: 8px;
    --product-label-line-height: 10px;
  }
}

@media (min-width: 1160px) {
  .product-label {
    --product-label-font-size: 12px;
    --product-label-line-height: 15px;
  }
}

@media (min-width: 1264px) {
  .product {
    &__product-image-overlay {
      border-radius: 10px;
    }
  }

  .product-labels {
    &__product-info-labels {
      top: 16px;
      right: 16px;
      left: 16px;
    }

    &__status-label {
      bottom: 16px;
      left: 16px;
    }
  }

  .button-add-to-cart {
    font-size: 16px;
    line-height: 28px;
  }

  .product-label {
    padding: 6px 12px;
    border-radius: 4px;
  }
}

@media (min-width: 1800px) {
  .product-label {
    --product-label-font-size: 14px;
    --product-label-line-height: 20px;
  }
}
</style>
